/**
 * Tenants API Response Mappers
 */
import { reverseMap, transformArray, transformKeys } from '@/shared/mappers/base.mapper';

import type { NotificationFrequency, Tenant } from '@/types';

// =============================================================================
// Field Mappings
// =============================================================================

const TENANT_FIELDS = {
  TenantId: 'tenantId',
  Name: 'name',
  Key: 'key',
  IsActive: 'isActive',
  AccessLevel: 'accessLevel',
  CreatedAt: 'createdAt',
  UpdatedAt: 'updatedAt',
};

const NOTIFICATION_FREQUENCY_FIELDS = {
  Id: 'id',
  Frequency: 'frequency',
  Days: 'days',
};

// =============================================================================
// Response Transformers
// =============================================================================

export const transformGetTenantsResponse = (response: unknown): Tenant[] => {
  const allTenants = transformArray(response, (item) => {
    const transformed = transformKeys<Tenant>(item, TENANT_FIELDS, {
      parseJson: false,
      recursive: true,
    });

    // Ensure tenantId is always a string
    if (transformed.tenantId !== undefined) {
      transformed.tenantId = String(transformed.tenantId);
    }

    return transformed;
  });

  // Filter to only show active tenants (matches dataProtect behavior)
  return allTenants.filter((tenant) => tenant.isActive === true);
};

export const transformGetTenantResponse = (response: unknown): Tenant => {
  // BUGFIX: Backend sometimes returns tenant wrapped in an array instead of a single object
  // Handle both cases: single object or array with one element
  const rawTenant = Array.isArray(response) ? response[0] : response;

  const transformed = transformKeys<Tenant>(rawTenant, TENANT_FIELDS, {
    parseJson: false,
    recursive: true,
  });

  // Ensure tenantId is always a string
  if (transformed.tenantId !== undefined) {
    transformed.tenantId = String(transformed.tenantId);
  }

  return transformed;
};

export const transformCreateTenantResponse = (response: unknown): Tenant => {
  const transformed = transformKeys<Tenant>(response, TENANT_FIELDS, {
    parseJson: false,
    recursive: true,
  });

  // Ensure tenantId is always a string
  if (transformed.tenantId !== undefined) {
    transformed.tenantId = String(transformed.tenantId);
  }

  return transformed;
};

export const transformGetNotificationFrequenciesResponse = (
  response: unknown
): NotificationFrequency[] => {
  return transformArray(response, (item) =>
    transformKeys<NotificationFrequency>(item, NOTIFICATION_FREQUENCY_FIELDS, {
      parseJson: false,
      recursive: true,
    })
  );
};

// =============================================================================
// Request Transformers
// =============================================================================

export const transformCreateTenantRequest = (data: unknown): unknown => {
  const reverseFields = reverseMap(TENANT_FIELDS);
  return transformKeys(data, reverseFields, {
    parseJson: false,
    recursive: true,
  });
};

export const transformInviteTenantUsersRequest = (data: unknown): unknown => {
  // Usually just passes through as-is, but transform if needed
  return data;
};
